import { useSessionStore } from "@auth/sessionStore";
import { isVisitCancelled, type TodayVisit } from "@visits/visitsClient";
import { useTodayVisits } from "@visits/visitsQueries";

/**
 * The one seam mapping a room to the patients currently in it. Derives from the
 * already-loaded today's-visits cache filtered by `currentRoomUuid`, so it issues no
 * fetch of its own and re-resolves whenever `/visits/today` refetches. Returns []
 * when no room is given or none is occupied.
 *
 * A room may hold several visits at once — a multi-pet appointment is N visits
 * sharing a room — ordered earliest-scheduled first. Cancelled visits are excluded
 * so a cancelled-but-still-roomed visit never counts as occupying a room. Views
 * depend on this hook rather than the data source, so if occupancy ever moves off
 * `/visits/today` only this changes.
 */
export const useRoomOccupants = (roomUuid: string | null): TodayVisit[] => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: visits } = useTodayVisits(sessionKey);
  return occupantsOf(visits, roomUuid);
};

/**
 * The predicate half of the hook above, over a list you already hold, so a caller
 * that can't subscribe (`useOpenRoom`) resolves occupancy by the same rule.
 */
export const occupantsOf = (
  visits: TodayVisit[],
  roomUuid: string | null,
): TodayVisit[] => {
  if (!roomUuid) return [];
  return visits.filter(
    (visit) => visit.currentRoomUuid === roomUuid && !isVisitCancelled(visit),
  );
};
